
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import PageLayout from '@/components/layout/PageLayout';
import { getBlogPosts } from '@/services/flotiqApi';
import BlogPostCard from '@/components/blog/BlogPostCard';
import { Skeleton } from '@/components/ui/skeleton';
import { enhanceForSocialCrawlers, isSocialCrawler } from '@/utils/crawlerUtils';

const BlogListingPage = () => {
  const { data: posts, isLoading, error } = useQuery({
    queryKey: ['blogPosts'],
    queryFn: getBlogPosts
  });

  // Sort posts by publication date (newest first)
  const sortedPosts = React.useMemo(() => {
    if (!posts) return [];
    
    return [...posts].sort((a, b) => {
      const dateA = new Date(a.publicationDate || a.publicationdate || 0);
      const dateB = new Date(b.publicationDate || b.publicationdate || 0);
      return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
    });
  }, [posts]);
  
  // Enhance meta tags for social crawlers when the component mounts
  React.useEffect(() => {
    enhanceForSocialCrawlers();
  }, []);
  
  const renderSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array(6).fill(0).map((_, i) => (
        <div key={i} className="flex flex-col">
          <Skeleton className="w-full h-56" />
          <Skeleton className="w-3/4 h-7 mt-4" />
          <Skeleton className="w-full h-4 mt-3" />
          <Skeleton className="w-full h-4 mt-2" />
          <Skeleton className="w-full h-4 mt-2" />
          <Skeleton className="w-1/3 h-4 mt-4" />
        </div>
      ))}
    </div>
  );

  // Special simplified render for crawlers to improve indexing
  if (isSocialCrawler()) {
    return (
      <div className="crawler-friendly-content">
        <h1>Astrem Blog - Latest Articles</h1>
        {!isLoading && !error && sortedPosts && sortedPosts.length > 0 ? (
          <div>
            {sortedPosts.map(post => (
              <div key={post.id} className="blog-post-item">
                <h2><a href={`/blog/${post.slug}`}>{post.title}</a></h2>
                {post.featuredImage && (
                  <img 
                    src={`https://api.flotiq.com/image/800/0/${post.featuredImage[0]?.id || post.featuredImage}.webp`} 
                    alt={post.title} 
                  />
                )}
                <p>{post.excerpt || (post.content?.substring(0, 150) + '...')}</p>
                <p>Published: {new Date(post.publicationDate || post.publicationdate || 0).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        ) : (
          <p>Loading blog posts...</p>
        )}
      </div>
    );
  }

  return (
    <PageLayout 
      title="Blog | Astrem - Websites, Content & Marketing Solutions"
      description="Explore the Astrem blog for insights on website design, photography, marketing, SEO, branding, and more to help grow your business."
      navStyle="default" // Change to default to have white text on black background
    >
      <Helmet>
        <title>Blog | Astrem - Websites, Content & Marketing Solutions</title>
        <meta name="description" content="Explore the Astrem blog for insights on website design, photography, marketing, SEO, branding, and more to help grow your business." />
        <meta property="og:title" content="Blog | Astrem - Websites, Content & Marketing Solutions" />
        <meta property="og:description" content="Explore the Astrem blog for insights on website design, photography, marketing, SEO, branding, and more to help grow your business." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/blog`} />
        <link rel="canonical" href={`${window.location.origin}/blog`} />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Astrem Blog",
            "url": "${window.location.origin}/blog",
            "description": "Explore the Astrem blog for insights on website design, photography, marketing, SEO, branding, and more to help grow your business.",
            "publisher": {
              "@type": "Organization",
              "name": "Astrem",
              "logo": {
                "@type": "ImageObject",
                "url": "${window.location.origin}/lovable-uploads/d023512a-50be-4738-9760-d5afcf453819.png"
              }
            }
          }
        `}</script>
      </Helmet>

      {/* Add black background hero section */}
      <div className="bg-astrem-black text-white pt-36 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Blog</h1>
            <p className="text-lg text-gray-300">
              Insights, guides, and stories about design, technology, marketing, and business growth.
            </p>
          </div>
        </div>
      </div>

      <section className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            renderSkeleton()
          ) : error ? (
            <div className="text-center text-red-500 py-10">
              <p className="text-xl">Failed to load blog posts.</p>
              <p className="mt-2">Please try again later.</p>
            </div>
          ) : sortedPosts && sortedPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedPosts.map(post => (
                <BlogPostCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-xl">No blog posts found.</p>
              <p className="mt-2">Check back soon for updates.</p>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

export default BlogListingPage;
